import React, { useRef, useState } from 'react';
import { User } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const ProfileAvatar = ({ 
  src, 
  name, 
  size = 'md',
  className = '' 
}) => {
  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-14 h-14 text-lg',
    xl: 'w-20 h-20 text-xl',
  };

  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-7 h-7',
    xl: 'w-10 h-10',
  };

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Build full image URL
  const getImageUrl = (photoPath) => {
    if (!photoPath) return null;
    if (photoPath.startsWith('http')) return photoPath;
    // Support both /uploads and /api/uploads paths
    if (photoPath.startsWith('/api/uploads') || photoPath.startsWith('/uploads')) {
      // Ensure path starts with /api/uploads for CORS support
      const normalizedPath = photoPath.startsWith('/api/') ? photoPath : `/api${photoPath}`;
      return `${API_BASE}${normalizedPath}`;
    }
    return photoPath;
  };

  const imageUrl = getImageUrl(src);

  return (
    <div 
      className={`
        ${sizeClasses[size]} 
        rounded-full overflow-hidden flex items-center justify-center
        bg-zinc-800 border border-zinc-700
        ${className}
      `}
    >
      {imageUrl ? (
        <img 
          src={imageUrl} 
          alt={name || 'Profile'} 
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      ) : null}
      <div 
        className={`
          ${imageUrl ? 'hidden' : 'flex'} 
          items-center justify-center w-full h-full
          bg-gradient-to-br from-[#ccff00]/20 to-[#ccff00]/5 text-[#ccff00] font-bold
        `}
      >
        {name ? getInitials(name) : <User className={iconSizes[size]} />}
      </div>
    </div>
  );
};

export const ProfilePhotoUpload = ({ 
  currentPhoto, 
  name,
  onUpload, 
  uploading = false 
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(file);
    }
    e.target.value = null;
  };

  const uploadFromCamera = async (source) => {
    try {
      const photo = await Camera.getPhoto({
        quality: 85,
        resultType: CameraResultType.Uri,
        source,
      });
      if (!photo?.webPath) {
        return;
      }
      const response = await fetch(photo.webPath);
      const blob = await response.blob();
      const file = new File([blob], `profile-${Date.now()}.jpg`, { type: blob.type || 'image/jpeg' });
      onUpload(file);
    } catch (error) {
      console.warn('Camera pick cancelled or failed:', error);
    }
  };

  const triggerInput = (ref) => {
    if (uploading) {
      return;
    }
    ref.current?.click();
    setTimeout(() => {
      setPickerOpen(false);
    }, 0);
  };

  return (
    <div className="flex flex-col items-center space-y-3">
      <div className="relative group">
        <ProfileAvatar src={currentPhoto} name={name} size="xl" />
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          disabled={uploading}
          className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer disabled:cursor-not-allowed"
          aria-label="Cambia foto profilo"
        >
          <span className="text-white text-xs font-medium">
            {uploading ? 'Caricamento...' : 'Cambia'}
          </span>
        </button>
      </div>
      <p className="text-zinc-500 text-xs">Clicca per cambiare foto</p>

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
        disabled={uploading}
      />
      <input
        ref={libraryInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
        disabled={uploading}
      />

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="bg-zinc-900 border border-white/10 rounded-xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Aggiorna foto profilo</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Button
              type="button"
              onClick={async () => {
                if (Capacitor.isNativePlatform()) {
                  setPickerOpen(false);
                  await uploadFromCamera(CameraSource.Camera);
                  return;
                }
                triggerInput(cameraInputRef);
              }}
              className="w-full bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl"
            >
              Scatta foto
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                if (Capacitor.isNativePlatform()) {
                  setPickerOpen(false);
                  await uploadFromCamera(CameraSource.Photos);
                  return;
                }
                triggerInput(libraryInputRef);
              }}
              className="w-full border-zinc-700 text-zinc-200 hover:bg-zinc-800 rounded-xl"
            >
              Scegli dalla galleria
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setPickerOpen(false)}
              className="w-full text-zinc-400 hover:text-white"
            >
              Annulla
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
